import React from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  Banknote,
  ClipboardCheck,
  FileCheck2,
  Languages,
  Scale,
  UserCheck,
  type LucideIcon,
} from 'lucide-react';
import { CAPABILITIES, type Capability, type CapabilityStatus } from './siteFacts';

/**
 * The six capabilities, drawn once.
 *
 * Every page that describes what WeaveIn does uses this and no other list, so
 * Platform, Developers and the audience pages cannot drift apart again (they
 * had four, six, eighteen and six different items between them). `mode`
 * chooses the level of detail: plain words for the people who do the work,
 * the integration surface for an engineer.
 */

const ICONS: Record<string, LucideIcon> = {
  trust: Languages,
  eligibility: UserCheck,
  encounter: ClipboardCheck,
  rules: Scale,
  disbursement: Banknote,
  reconciliation: FileCheck2,
};

const STATUS_STYLE: Record<CapabilityStatus, string> = {
  Live: 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-300 dark:border-emerald-500/30',
  Pilot: 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-500/10 dark:text-amber-300 dark:border-amber-500/30',
  Planned: 'bg-gray-50 text-gray-600 border-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-700',
};

export const StatusPill: React.FC<{ status: CapabilityStatus }> = ({ status }) => (
  <span className={`inline-block text-xs font-semibold px-2 py-0.5 rounded-full border ${STATUS_STYLE[status]}`}>
    {status}
  </span>
);

interface CapabilitySpineProps {
  mode?: 'plain' | 'spec';
  heading?: string;
  lede?: string;
  /** Show who signs off at each step. */
  showApproves?: boolean;
  showStatus?: boolean;
  /** Only these capability keys, in the canonical order. */
  only?: string[];
  linkToPlatform?: boolean;
}

const CapabilitySpine: React.FC<CapabilitySpineProps> = ({
  mode = 'plain',
  heading = 'Six capabilities. Every funder behind them.',
  lede = 'An agent drafts each step. A named person approves it. Nothing reaches a funder unreviewed.',
  showApproves = mode === 'plain',
  showStatus = false,
  only,
  linkToPlatform = false,
}) => {
  const items: Capability[] = only ? CAPABILITIES.filter((c) => only.includes(c.key)) : CAPABILITIES;

  return (
    <section className="py-16 bg-white dark:bg-gray-950">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="text-2xl md:text-3xl font-bold text-ink mb-3 text-center">
          {heading}
        </h2>
        {lede && (
          <p className="text-base text-gray-600 dark:text-gray-300 text-center max-w-2xl mx-auto mb-10">{lede}</p>
        )}
        <ol className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {items.map((c) => {
            const Icon = ICONS[c.key] ?? Scale;
            const n = CAPABILITIES.indexOf(c) + 1;
            return (
              <li
                key={c.key}
                className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-6 flex flex-col"
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <span className="w-9 h-9 rounded-lg bg-v2accent-soft flex items-center justify-center">
                      <Icon className="w-5 h-5 text-v2accent" aria-hidden="true" />
                    </span>
                    <span className="text-xs font-semibold tracking-wider text-gray-400">0{n}</span>
                  </div>
                  {showStatus && <StatusPill status={c.status} />}
                </div>
                <h3 className="font-bold text-gray-900 dark:text-gray-100 mb-2">{c.name}</h3>
                <p className="text-sm text-gray-600 dark:text-gray-300 flex-1">{mode === 'plain' ? c.plain : c.spec}</p>
                {showApproves && (
                  <p className="mt-4 pt-3 border-t border-gray-100 dark:border-gray-800 text-xs text-gray-500 dark:text-gray-400">
                    <span className="font-semibold text-gray-700 dark:text-gray-200">Person decides: </span>
                    {c.approves}
                  </p>
                )}
              </li>
            );
          })}
        </ol>
        {linkToPlatform && (
          <div className="text-center mt-8">
            <Link to="/platform" className="inline-flex items-center text-ink font-semibold hover:underline">
              How each capability works
              <ArrowRight className="w-4 h-4 ml-1" />
            </Link>
          </div>
        )}
      </div>
    </section>
  );
};

export default CapabilitySpine;
